import React, { useMemo } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import * as wasm from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { Link } from 'react-router-dom';
import { useSchedule, FuzzyScheduleResult } from '@/hooks/useSchedule';

type TaskOrOutcome = {
  type: "task" | "outcome";
  id: string;
}

/**
 * Find unschedulable warnings for a specific task or outcome from the fuzzy schedule.
 * The fuzzy schedule's `schedule.unschedulable` maps node IDs to `{ label, reason }`.
 * We need to match via `meta.node_to_entity` to find the relevant node ID.
 */
function findWarnings(
  schedule: FuzzyScheduleResult | null,
  entityType: 'Task' | 'Outcome',
  entityId: string,
): { reason: string; label: string } | null {
  if (!schedule) return null;
  const meta = (schedule as any).meta;
  const unschedulable = (schedule as any).schedule?.unschedulable ?? {};
  const nodeToEntity = meta?.node_to_entity ?? {};

  for (const [nodeId, entity] of Object.entries(nodeToEntity)) {
    if ((entity as any)?.type === entityType && (entity as any)?.id === entityId) {
      const item = unschedulable[nodeId] as any;
      if (item) {
        return { reason: item.reason, label: item.label };
      }
    }
  }
  return null;
}

export const ScheduleWarningsListEntry: React.FC<{ taskOrOutcome: TaskOrOutcome }> = (props) => {
  const { schedule } = useSchedule();
  const { taskOrOutcome } = props;

  const warning = useMemo(() => {
    return findWarnings(
      schedule,
      taskOrOutcome.type === 'task' ? 'Task' : 'Outcome',
      taskOrOutcome.id,
    );
  }, [schedule, taskOrOutcome.type, taskOrOutcome.id]);

  if (!warning) return null;

  return (
    <div className="flex items-start gap-2 text-yellow-600 dark:text-yellow-400">
      <FiAlertTriangle className="mt-0.5 flex-shrink-0" />
      <div className="text-sm">{warning.reason}</div>
    </div>
  );
};

export default ScheduleWarningsListEntry;
